import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { CheckCircle, AlertCircle } from 'lucide-react'

interface SuccessorMapItem {
  from_id: number
  from_name: string
  member_count: number
  successors: Array<{ id: number; name: string }>
  status: 'ok' | 'suggested' | 'missing'
  suggested_successor?: { id: number; name: string } | null
  suggested_successor_name?: string | null
  member_breakdown?: { direct_members: number; pool_members: number }
}

interface UnitOption {
  id: number
  class_name?: string
  name?: string
}

interface RowNotice {
  tone: 'success' | 'info' | 'warning'
  text: string
}

interface Props {
  unitType: 'class' | 'course' | 'group'
  title: string
  description?: string
  required?: boolean
  includeMemberBreakdown?: boolean
  skipped?: boolean
  onSkipChange?: (skip: boolean) => void
  onReadinessChange?: (ready: boolean) => void
}

export default function SuccessorMapGrid({
  unitType,
  title,
  description,
  required = false,
  includeMemberBreakdown = false,
  skipped = false,
  onSkipChange,
  onReadinessChange
}: Props) {
  const queryClient = useQueryClient()
  const [inlineSelection, setInlineSelection] = useState<Record<number, number | ''>>({})
  const [manualClassName, setManualClassName] = useState<Record<number, string>>({})
  const [rowNotices, setRowNotices] = useState<Record<number, RowNotice>>({})

  const { data: mapResponse, isLoading } = useQuery(
    ['transitions-successor-map', unitType, includeMemberBreakdown],
    () =>
      api.get('/transitions/successor-map', {
        params: {
          unit_type: unitType,
          include_member_breakdown: includeMemberBreakdown ? 'true' : 'false'
        }
      }).then(res => res.data),
    { staleTime: 30_000 }
  )

  const items: SuccessorMapItem[] = mapResponse?.data || []

  const { data: unitsData } = useQuery(
    ['units-list-successor-map', unitType],
    () => api.get(`/units/${unitType}`, { params: { is_active: 'true' } }).then(res => res.data),
    { staleTime: 60_000 }
  )
  const unitNameKey = unitType === 'class' ? 'class_name' : 'name'
  const allUnits: UnitOption[] = unitsData?.data || []

  const suggestedPairs = useMemo(
    () =>
      items
        .filter(item => item.status === 'suggested' && item.suggested_successor?.id)
        .map(item => ({ from_id: item.from_id, to_id: item.suggested_successor!.id })),
    [items]
  )

  const isReady = useMemo(() => {
    if (!required) return true
    if (skipped) return true
    return items.length > 0 && items.every(item => item.status === 'ok')
  }, [items, required, skipped])

  const completionText = useMemo(() => {
    const readyCount = items.filter(item => item.status === 'ok').length
    return `${readyCount} von ${items.length} Einheiten bereit`
  }, [items])

  const bulkCreateMutation = useMutation(
    (pairs: Array<{ from_id: number; to_id: number }>) =>
      api.post('/units/successors-bulk', { unit_type: unitType, pairs }).then(res => res.data),
    {
      onSuccess: (result: any) => {
        const created = result?.data?.created || 0
        toast.success(`${created} Folgebeziehungen angelegt`)
        queryClient.invalidateQueries(['transitions-successor-map', unitType, includeMemberBreakdown])
        queryClient.invalidateQueries(['transitions-suggestions-batch'])
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Vorschläge konnten nicht angelegt werden')
      }
    }
  )

  const createSingleMutation = useMutation(
    ({ fromId, toId }: { fromId: number; toId: number }) =>
      api.post(`/units/${unitType}/${fromId}/successors`, {
        successor_type: unitType,
        successor_id: toId
      }),
    {
      onSuccess: (_data, variables) => {
        const target =
          unitType === 'class' && variables.toId === -1
            ? 'attic'
            : String(
                allUnits.find((u: any) => u.id === variables.toId)?.[unitNameKey] ??
                  `#${variables.toId}`
              )
        setRowNotices((prev) => ({
          ...prev,
          [variables.fromId]: {
            tone: 'success',
            text: `Verknüpft mit ${target}.`
          }
        }))
        toast.success('Folgebeziehung gespeichert')
        queryClient.invalidateQueries(['transitions-successor-map', unitType, includeMemberBreakdown])
        queryClient.invalidateQueries(['transitions-suggestions-batch'])
      },
      onError: (error: any, variables) => {
        if (error?.response?.status === 409) {
          setRowNotices((prev) => ({
            ...prev,
            [variables.fromId]: {
              tone: 'info',
              text: 'Diese Verknüpfung existiert bereits.'
            }
          }))
        }
        toast.error(error?.response?.data?.error || 'Folgebeziehung konnte nicht gespeichert werden')
      }
    }
  )

  const createClassAndLinkMutation = useMutation(
    async ({ fromId, targetName }: { fromId: number; targetName: string }) => {
      const normalized = String(targetName || '').trim()
      if (!normalized) throw new Error('Bitte Klassennamen eingeben')

      // Erst prüfen, dann ggf. erzeugen
      let targetId: number | null =
        allUnits.find((u: any) => String(u.class_name || '').trim().toLowerCase() === normalized.toLowerCase())?.id ?? null

      let created = false
      if (!targetId) {
        try {
          const createRes = await api.post('/units/class', { class_name: normalized, is_active: true })
          targetId = createRes?.data?.data?.id ?? null
          created = true
        } catch (error: any) {
          const candidatesRes = await api.get('/units/class', { params: { is_active: 'true', search: normalized } })
          const candidates = candidatesRes?.data?.data || []
          const existing = candidates.find((u: any) => String(u.class_name || '').trim().toLowerCase() === normalized.toLowerCase())
          if (!existing?.id) throw error
          targetId = existing.id
        }
      }
      if (!targetId) throw new Error('Zielklasse konnte nicht ermittelt werden')
      await api.post(`/units/class/${fromId}/successors`, {
        successor_type: 'class',
        successor_id: targetId
      })
      return { targetName: normalized, fromId, created }
    },
    {
      onSuccess: ({ targetName, fromId, created }) => {
        setRowNotices((prev) => ({
          ...prev,
          [fromId]: {
            tone: created ? 'success' : 'info',
            text: created
              ? `${targetName} wurde neu angelegt und verknüpft.`
              : `${targetName} war bereits vorhanden und wurde verknüpft.`
          }
        }))
        setManualClassName((prev) => ({ ...prev, [fromId]: '' }))
        toast.success(`Zielklasse ${targetName} ${created ? 'angelegt' : 'gefunden'} und verknüpft`)
        queryClient.invalidateQueries(['class'])
        queryClient.invalidateQueries(['transitions-successor-map', unitType, includeMemberBreakdown])
        queryClient.invalidateQueries(['transitions-suggestions-batch'])
      },
      onError: (error: any, variables) => {
        if (error?.response?.status === 409) {
          setRowNotices((prev) => ({
            ...prev,
            [variables.fromId]: {
              tone: 'info',
              text: `Verknüpfung zu ${variables.targetName} existiert bereits.`
            }
          }))
        } else {
          setRowNotices((prev) => ({
            ...prev,
            [variables.fromId]: {
              tone: 'warning',
              text: `Konnte ${variables.targetName} nicht verknüpfen.`
            }
          }))
        }
        toast.error(error?.response?.data?.error || 'Zielklasse konnte nicht automatisch angelegt werden')
      }
    }
  )

  useEffect(() => {
    if (onReadinessChange) onReadinessChange(isReady)
  }, [isReady, onReadinessChange])

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{title}</h3>
          {description && <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>}
        </div>
        {onSkipChange && (
          <button type="button" onClick={() => onSkipChange(!skipped)} className="btn btn-secondary">
            {skipped ? 'Kurse bearbeiten' : 'Kurse überspringen'}
          </button>
        )}
      </div>

      {!skipped && suggestedPairs.length > 0 && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => bulkCreateMutation.mutate(suggestedPairs)}
          disabled={bulkCreateMutation.isLoading}
        >
          {bulkCreateMutation.isLoading
            ? 'Vorschläge werden angelegt...'
            : `Alle Vorschläge anwenden (${suggestedPairs.length})`}
        </button>
      )}

      {!skipped && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
          {completionText}
        </div>
      )}

      {skipped ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
          Dieser Schritt wird übersprungen.
        </div>
      ) : isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nachfolgerkarte wird geladen...</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((item) => {
            const hasSuccessor = item.successors.length > 0
            const selected = inlineSelection[item.from_id] ?? ''
            const successorName = hasSuccessor ? item.successors[0].name : null
            const cardClass =
              item.status === 'ok'
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40'
                : item.status === 'suggested'
                  ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
                  : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40'
            return (
              <div key={item.from_id} className={`rounded-lg border p-4 ${cardClass}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-gray-900 dark:text-gray-50">
                    {item.from_name} <span className="text-gray-500">→</span> {successorName || 'Noch offen'}
                  </div>
                  <span className="rounded bg-white/80 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-900/80 dark:text-gray-200">
                    {item.member_count} Lernende
                  </span>
                </div>
                {unitType === 'course' && item.member_breakdown && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    Direkt: {item.member_breakdown.direct_members} · Klassen-Pool: {item.member_breakdown.pool_members}
                  </p>
                )}
                {item.status === 'ok' ? (
                  <p className="mt-2 flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                    <CheckCircle className="h-3.5 w-3.5" /> Folgeeinheit vorhanden
                  </p>
                ) : item.status === 'suggested' ? (
                  <p className="mt-2 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-3.5 w-3.5" /> Vorschlag: {item.suggested_successor?.name}
                  </p>
                ) : (
                  <p className="mt-2 flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
                    <AlertCircle className="h-3.5 w-3.5" /> Manuelle Auswahl erforderlich
                  </p>
                )}
                {!hasSuccessor && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select
                        value={selected}
                        onChange={(e) => setInlineSelection((prev) => ({ ...prev, [item.from_id]: e.target.value ? parseInt(e.target.value, 10) : '' }))}
                        className="rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
                      >
                        <option value="">Folgeklasse/Folgekurs wählen</option>
                        {unitType === 'class' && (
                          <option value={-1}>Attic (Abschlussklasse)</option>
                        )}
                        {allUnits.map((u) => (
                          <option key={u.id} value={u.id}>
                            {(u as any)[unitNameKey]}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!selected || createSingleMutation.isLoading}
                        onClick={() => {
                          if (!selected) return
                          if (unitType === 'class' && Number(selected) === -1) {
                            createClassAndLinkMutation.mutate({
                              fromId: item.from_id,
                              targetName: 'attic'
                            })
                            return
                          }
                          createSingleMutation.mutate({ fromId: item.from_id, toId: Number(selected) })
                        }}
                      >
                        Speichern
                      </button>
                      {unitType === 'class' && item.suggested_successor_name && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={createClassAndLinkMutation.isLoading}
                          onClick={() =>
                            createClassAndLinkMutation.mutate({
                              fromId: item.from_id,
                              targetName: item.suggested_successor_name as string
                            })
                          }
                        >
                          {createClassAndLinkMutation.isLoading
                            ? 'Wird angelegt...'
                            : `${item.suggested_successor_name} neu anlegen`}
                        </button>
                      )}
                    </div>
                    {unitType === 'class' && (
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                          type="text"
                          value={manualClassName[item.from_id] || ''}
                          onChange={(e) =>
                            setManualClassName((prev) => ({ ...prev, [item.from_id]: e.target.value }))
                          }
                          placeholder="Neue Folgeklasse, z. B. 07g"
                          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={createClassAndLinkMutation.isLoading || !(manualClassName[item.from_id] || '').trim()}
                          onClick={() =>
                            createClassAndLinkMutation.mutate({
                              fromId: item.from_id,
                              targetName: (manualClassName[item.from_id] || '').trim()
                            })
                          }
                        >
                          Neu anlegen und verknüpfen
                        </button>
                      </div>
                    )}
                    {rowNotices[item.from_id] && (
                      <p
                        className={`text-xs ${
                          rowNotices[item.from_id].tone === 'success'
                            ? 'text-green-700 dark:text-green-300'
                            : rowNotices[item.from_id].tone === 'warning'
                              ? 'text-red-700 dark:text-red-300'
                              : 'text-blue-700 dark:text-blue-300'
                        }`}
                      >
                        {rowNotices[item.from_id].text}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
